import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Clock, MapPin, Users, CheckCircle, ArrowRight, ArrowLeft, Tag, CreditCard } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import './Book.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const eventTypes = [
  'Wedding', 'Corporate Event', 'Birthday Party', 'Quinceañera', 
  'Holiday Party', 'Baby Shower', 'Graduation', 'Prom', 'Anniversary', 'Other'
];

const timeSlots = [
  '10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM',
  '3:00 PM', '4:00 PM', '5:00 PM', '6:00 PM', '7:00 PM', '8:00 PM'
];

function BookingForm() {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const stripe = useStripe();
  const elements = useElements();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [packages, setPackages] = useState([]);
  const [addons, setAddons] = useState([]);
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(null);
  const [promoError, setPromoError] = useState('');

  const [formData, setFormData] = useState({
    // Customer Info
    customer_name: user?.name || '',
    customer_email: user?.email || '',
    customer_phone: '',
    // Event Details
    event_type: '',
    event_date: '',
    event_time: '',
    event_location: '',
    guest_count: 50,
    // Package & Addons
    package_id: '',
    addon_ids: [],
    // Payment
    payment_type: 'full',
    special_requests: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [packagesData, addonsData] = await Promise.all([
        api.getPackages(),
        api.getAddons(),
      ]);
      setPackages(packagesData);
      setAddons(addonsData);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const selectedPackage = packages.find(p => p.id === formData.package_id);
  const selectedAddons = addons.filter(a => formData.addon_ids.includes(a.id));

  const calculateTotal = () => {
    let total = selectedPackage?.price || 0;
    selectedAddons.forEach(addon => {
      total += addon.price;
    });
    if (promoApplied) {
      total = total * (1 - promoApplied.discount / 100);
    }
    return total;
  };

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoError('');
    try {
      const result = await api.validatePromoCode(promoCode.toUpperCase(), calculateTotal());
      setPromoApplied(result);
    } catch (error) {
      setPromoError(error.message || 'Invalid promo code');
      setPromoApplied(null);
    }
  };

  const handleRemovePromo = () => {
    setPromoCode('');
    setPromoApplied(null);
  };

  const handleAddonToggle = (addonId) => {
    setFormData(prev => ({
      ...prev,
      addon_ids: prev.addon_ids.includes(addonId)
        ? prev.addon_ids.filter(id => id !== addonId)
        : [...prev.addon_ids, addonId]
    }));
  };

  const isStepValid = () => {
    switch (step) {
      case 1:
        return formData.customer_name && formData.customer_email && formData.customer_phone;
      case 2:
        return formData.event_type && formData.event_date && formData.event_time && formData.event_location;
      case 3:
        return formData.package_id;
      case 4:
        return true;
      default:
        return false;
    }
  };

  const handleSubmit = async () => {
    if (!stripe || !elements) return;
    setLoading(true);

    try {
      // Create booking
      const booking = await api.createBooking({
        ...formData,
        total_amount: calculateTotal(),
      });

      // Save user
      login({ email: formData.customer_email, name: formData.customer_name });

      // Create payment intent
      const { client_secret } = await api.createPaymentIntent(booking.id, formData.payment_type);

      // Confirm payment
      const { error, paymentIntent } = await stripe.confirmCardPayment(client_secret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name: formData.customer_name,
            email: formData.customer_email,
          },
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      if (paymentIntent.status === 'succeeded') {
        await api.confirmPayment(booking.id, paymentIntent.id);
        navigate('/booking-confirmed', { state: { booking } });
      }
    } catch (error) {
      alert(error.message || 'Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <motion.div
            key="step1"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="booking-step"
          >
            <h2>Your Information</h2>
            <div className="form-group">
              <label className="form-label">Full Name</label>
              <input
                type="text"
                className="form-input"
                value={formData.customer_name}
                onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                placeholder="John Doe"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                className="form-input"
                value={formData.customer_email}
                onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                placeholder="john@example.com"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Phone</label>
              <input
                type="tel"
                className="form-input"
                value={formData.customer_phone}
                onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                placeholder="(555) 123-4567"
              />
            </div>
          </motion.div>
        );

      case 2:
        return (
          <motion.div
            key="step2"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="booking-step"
          >
            <h2>Event Details</h2>
            <div className="form-group">
              <label className="form-label">Event Type</label>
              <select
                className="form-input form-select"
                value={formData.event_type}
                onChange={(e) => setFormData({ ...formData, event_type: e.target.value })}
              >
                <option value="">Select event type</option>
                {eventTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Event Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={formData.event_date}
                  onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Event Time</label>
                <select
                  className="form-input form-select"
                  value={formData.event_time}
                  onChange={(e) => setFormData({ ...formData, event_time: e.target.value })}
                >
                  <option value="">Select time</option>
                  {timeSlots.map(time => (
                    <option key={time} value={time}>{time}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Event Location</label>
              <input
                type="text"
                className="form-input"
                value={formData.event_location}
                onChange={(e) => setFormData({ ...formData, event_location: e.target.value })}
                placeholder="Venue name and address"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Estimated Guest Count</label>
              <input
                type="number"
                className="form-input"
                value={formData.guest_count}
                onChange={(e) => setFormData({ ...formData, guest_count: parseInt(e.target.value) })}
                min="1"
              />
            </div>
          </motion.div>
        );

      case 3:
        return (
          <motion.div
            key="step3"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="booking-step"
          >
            <h2>Select Package</h2>
            <div className="packages-selection">
              {packages.map(pkg => (
                <div
                  key={pkg.id}
                  className={`package-option ${formData.package_id === pkg.id ? 'selected' : ''}`}
                  onClick={() => setFormData({ ...formData, package_id: pkg.id })}
                >
                  {pkg.popular && <span className="popular-badge">Popular</span>}
                  <h3>{pkg.name}</h3>
                  <span className="package-price">${pkg.price}</span>
                  <p>{pkg.description}</p>
                  <span className="package-duration">{pkg.duration}</span>
                  {formData.package_id === pkg.id && (
                    <CheckCircle size={24} className="check-icon" />
                  )}
                </div>
              ))}
            </div>

            <h3 className="addons-title">Add-Ons (Optional)</h3>
            <div className="addons-selection">
              {addons.map(addon => (
                <div
                  key={addon.id}
                  className={`addon-option ${formData.addon_ids.includes(addon.id) ? 'selected' : ''}`}
                  onClick={() => handleAddonToggle(addon.id)}
                >
                  <div>
                    <h4>{addon.name}</h4>
                    <p>{addon.description}</p>
                  </div>
                  <div className="addon-right">
                    <span className="addon-price">+${addon.price}</span>
                    <div className={`addon-checkbox ${formData.addon_ids.includes(addon.id) ? 'checked' : ''}`}>
                      {formData.addon_ids.includes(addon.id) && <CheckCircle size={16} />}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        );

      case 4:
        return (
          <motion.div
            key="step4"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="booking-step"
          >
            <h2>Review & Payment</h2>

            <div className="booking-summary">
              <div className="summary-section">
                <h4>Customer</h4>
                <p>{formData.customer_name}</p>
                <p className="text-gray">{formData.customer_email}</p>
              </div>

              <div className="summary-section">
                <h4>Event Details</h4>
                <div className="summary-row">
                  <Calendar size={16} /> {formData.event_date}
                </div>
                <div className="summary-row">
                  <Clock size={16} /> {formData.event_time}
                </div>
                <div className="summary-row">
                  <MapPin size={16} /> {formData.event_location}
                </div>
                <div className="summary-row">
                  <Users size={16} /> {formData.guest_count} guests
                </div>
              </div>

              <div className="summary-section">
                <h4>Package</h4>
                <p>{selectedPackage?.name} - ${selectedPackage?.price}</p>
                {selectedAddons.length > 0 && (
                  <>
                    <h4 className="mt-4">Add-Ons</h4>
                    {selectedAddons.map(addon => (
                      <p key={addon.id}>{addon.name} - ${addon.price}</p>
                    ))}
                  </>
                )}
              </div>

              {/* Promo Code */}
              <div className="promo-section">
                <h4>Promo Code</h4>
                {promoApplied ? (
                  <div className="promo-applied">
                    <Tag size={16} />
                    <span>{promoApplied.code} - {promoApplied.discount}% OFF</span>
                    <button onClick={handleRemovePromo} className="remove-promo">Remove</button>
                  </div>
                ) : (
                  <div className="promo-input">
                    <input
                      type="text"
                      placeholder="Enter code"
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                      className="form-input"
                    />
                    <button onClick={handleApplyPromo} className="btn btn-secondary">Apply</button>
                  </div>
                )}
                {promoError && <p className="promo-error">{promoError}</p>}
              </div>

              <div className="summary-total">
                <span>Total</span>
                <span className="total-amount">${calculateTotal().toFixed(2)}</span>
              </div>
            </div>

            <div className="payment-options">
              <h4>Payment Option</h4>
              <div className="payment-toggle">
                <button
                  className={`toggle-btn ${formData.payment_type === 'full' ? 'active' : ''}`}
                  onClick={() => setFormData({ ...formData, payment_type: 'full' })}
                >
                  Pay in Full
                </button>
                <button
                  className={`toggle-btn ${formData.payment_type === 'deposit' ? 'active' : ''}`}
                  onClick={() => setFormData({ ...formData, payment_type: 'deposit' })}
                >
                  50% Deposit
                </button>
              </div>
              <p className="payment-info">
                {formData.payment_type === 'deposit'
                  ? `Pay $${(calculateTotal() * 0.5).toFixed(2)} now, remaining due before event`
                  : `Pay $${calculateTotal().toFixed(2)} today`}
              </p>
            </div>

            <div className="form-group">
              <label className="form-label">Special Requests (Optional)</label>
              <textarea
                className="form-input form-textarea"
                rows={3}
                value={formData.special_requests}
                onChange={(e) => setFormData({ ...formData, special_requests: e.target.value })}
                placeholder="Any special requests or notes..."
              />
            </div>

            <div className="form-group">
              <label className="form-label">Card Details</label>
              <div className="card-input">
                <CardElement
                  options={{
                    style: {
                      base: {
                        fontSize: '16px',
                        color: '#F8F5F0',
                        '::placeholder': { color: '#6a6a6a' },
                      },
                    },
                  }}
                />
              </div>
            </div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="booking-container">
      {/* Progress */}
      <div className="booking-progress">
        {['Your Info', 'Event Details', 'Package', 'Payment'].map((label, index) => (
          <div
            key={index}
            className={`progress-step ${step > index + 1 ? 'completed' : ''} ${step === index + 1 ? 'active' : ''}`}
          >
            <div className="step-number">{step > index + 1 ? <CheckCircle size={16} /> : index + 1}</div>
            <span>{label}</span>
          </div>
        ))}
      </div>

      {/* Form */}
      <div className="booking-form">
        <AnimatePresence mode="wait">
          {renderStep()}
        </AnimatePresence>

        <div className="booking-nav">
          {step > 1 && (
            <button className="btn btn-secondary" onClick={() => setStep(step - 1)}>
              <ArrowLeft size={18} />
              Back
            </button>
          )}
          {step < 4 ? (
            <button
              className="btn btn-primary"
              onClick={() => setStep(step + 1)}
              disabled={!isStepValid()}
            >
              Continue
              <ArrowRight size={18} />
            </button>
          ) : (
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={loading || !stripe}
            >
              {loading ? 'Processing...' : 'Complete Booking'}
              <CreditCard size={18} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Book() {
  return (
    <div className="book-page">
      <section className="book-hero">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="section-subtitle">Start Booking</span>
            <h1 className="page-title">Book Your Event</h1>
            <p className="page-description">
              Complete the form below to reserve your photo booth experience.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <Elements stripe={stripePromise}>
            <BookingForm />
          </Elements>
        </div>
      </section>
    </div>
  );
}
